/**
 * Primitive Design Tokens - Base color scales, spacing, and typography values
 * These are the foundational tokens that semantic tokens reference
 */

// Color scales (0-50-100-200-300-400-500-600-700-800-900-950)
export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

export interface PrimitiveColorTokens {
  // Core color scales
  gray: ColorScale;
  red: ColorScale;
  blue: ColorScale;
  orange: ColorScale;
  green: ColorScale;
  purple: ColorScale;
  
  // Special colors (single values for special cases)
  white: string;
  black: string;
  transparent: string;
}

export interface PrimitiveSpacingTokens {
  px: string;    // 1px
  0: string;     // 0
  0.5: string;   // 0.125rem
  1: string;     // 0.25rem
  1.5: string;   // 0.375rem
  2: string;     // 0.5rem
  2.5: string;   // 0.625rem
  3: string;     // 0.75rem
  3.5: string;   // 0.875rem
  4: string;     // 1rem
  5: string;     // 1.25rem
  6: string;     // 1.5rem
  7: string;     // 1.75rem
  8: string;     // 2rem
  9: string;     // 2.25rem
  10: string;    // 2.5rem
  11: string;    // 2.75rem
  12: string;    // 3rem
  14: string;    // 3.5rem
  16: string;    // 4rem
  20: string;    // 5rem
  24: string;    // 6rem
  28: string;    // 7rem
  32: string;    // 8rem
  36: string;    // 9rem
  40: string;    // 10rem
  44: string;    // 11rem
  48: string;    // 12rem
  52: string;    // 13rem
  56: string;    // 14rem
  60: string;    // 15rem
  64: string;    // 16rem
  72: string;    // 18rem
  80: string;    // 20rem
  96: string;    // 24rem
}

export interface PrimitiveTypographyTokens {
  fontFamilies: {
    sans: string[];
    serif: string[];
    mono: string[];
  };
  fontSizes: {
    xs: string;    // 0.75rem
    sm: string;    // 0.875rem
    base: string;  // 1rem
    lg: string;    // 1.125rem
    xl: string;    // 1.25rem
    '2xl': string; // 1.5rem
    '3xl': string; // 1.875rem
    '4xl': string; // 2.25rem
    '5xl': string; // 3rem
    '6xl': string; // 3.75rem
    '7xl': string; // 4.5rem
    '8xl': string; // 6rem
    '9xl': string; // 8rem
  };
  fontWeights: {
    thin: number;       // 100
    extralight: number; // 200
    light: number;      // 300
    normal: number;     // 400
    medium: number;     // 500
    semibold: number;   // 600
    bold: number;       // 700
    extrabold: number;  // 800
    black: number;      // 900
  };
  lineHeights: {
    none: number;     // 1
    tight: number;    // 1.25
    snug: number;     // 1.375
    normal: number;   // 1.5
    relaxed: number;  // 1.625
    loose: number;    // 2
  };
  letterSpacing: {
    tighter: string;  // -0.05em
    tight: string;    // -0.025em
    normal: string;   // 0em
    wide: string;     // 0.025em
    wider: string;    // 0.05em
    widest: string;   // 0.1em
  };
}

export interface PrimitiveBorderTokens {
  radius: {
    none: string;   // 0
    sm: string;     // 0.125rem
    base: string;   // 0.25rem
    md: string;     // 0.375rem
    lg: string;     // 0.5rem
    xl: string;     // 0.75rem
    '2xl': string;  // 1rem
    '3xl': string;  // 1.5rem
    full: string;   // 9999px
  };
  width: {
    0: string;      // 0
    base: string;   // 1px
    2: string;      // 2px
    4: string;      // 4px
    8: string;      // 8px
  };
}

export interface PrimitiveShadowTokens {
  sm: string;
  base: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  inner: string;
  none: string;
}

export interface PrimitiveAnimationTokens {
  duration: {
    75: string;    // 75ms
    100: string;   // 100ms
    150: string;   // 150ms
    200: string;   // 200ms
    300: string;   // 300ms
    500: string;   // 500ms
    700: string;   // 700ms
    1000: string;  // 1000ms
  };
  easing: {
    linear: string;      // linear
    in: string;          // cubic-bezier(0.4, 0, 1, 1)
    out: string;         // cubic-bezier(0, 0, 0.2, 1)
    inOut: string;       // cubic-bezier(0.4, 0, 0.2, 1)
    ease: string;        // ease
    easeIn: string;      // ease-in
    easeOut: string;     // ease-out
    easeInOut: string;   // ease-in-out
  };
}

export interface PrimitiveZIndexTokens {
  auto: string;     // auto
  0: number;        // 0
  10: number;       // 10
  20: number;       // 20
  30: number;       // 30
  40: number;       // 40
  50: number;       // 50
  dropdown: number; // 1000
  sticky: number;   // 1020
  fixed: number;    // 1030
  modal: number;    // 1040
  popover: number;  // 1050
  tooltip: number;  // 1060
}

// Main primitive tokens interface
export interface PrimitiveTokens {
  colors: PrimitiveColorTokens;
  spacing: PrimitiveSpacingTokens;
  typography: PrimitiveTypographyTokens;
  borders: PrimitiveBorderTokens;
  shadows: PrimitiveShadowTokens;
  animations: PrimitiveAnimationTokens;
  zIndex: PrimitiveZIndexTokens;
}

// =============================================================================
// PRIMITIVE TOKEN IMPLEMENTATIONS  
// Based on current variables.css values and extended for comprehensive theming
// =============================================================================

// Color scales based on current theme colors and extended for proper scale coverage
export const primitiveColors: PrimitiveColorTokens = {
  // Gray scale - based on current backgrounds and text colors
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6', 
    200: '#e5e7eb',
    300: '#d1d5db',  // Close to --semantic-colors-text-secondary: #b8c5d6
    400: '#9ca3af',
    500: '#6b7280',  // --semantic-colors-text-secondary: #7a8394
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',  // Close to --semantic-colors-background-secondary: #16213e  
    900: '#111827',  // Close to --semantic-colors-background-primary: #1a1a2e
    950: '#030712'
  },

  // Red scale - based on --semantic-colors-interactive-primary: #e94560 and --semantic-colors-rpg-hp: #e74c3c
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',  // Close to current red colors
    600: '#dc2626',  // --semantic-colors-rpg-hp: #e74c3c
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a'
  },

  // Blue scale - based on --semantic-colors-interactive-secondary: #3498db and --semantic-colors-rpg-armor: #3498db
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',  // --semantic-colors-interactive-secondary: #3498db (close)
    600: '#2563eb',  // --semantic-colors-rpg-armor: #3498db (close)
    700: '#1d4ed8',  // --semantic-colors-rpg-focus: #2980b9 (close)
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554'
  },

  // Orange scale - based on --semantic-colors-interactive-primary: #f39c12 and --semantic-colors-rpg-hope: #f39c12
  orange: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',  // --semantic-colors-interactive-primary: #f39c12 (close)
    600: '#ea580c',  // --semantic-colors-rpg-hope: #f39c12 (close) 
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
    950: '#431407'
  },

  // Green scale - based on --semantic-colors-status-success: #28a745
  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',  // --semantic-colors-status-success: #28a745 (close)
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
    950: '#052e16'
  },

  // Purple scale - based on --semantic-colors-rpg-stress: #9b59b6
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',  // --semantic-colors-rpg-stress: #9b59b6 (close)
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
    950: '#3b0764'
  },

  // Special colors
  white: '#ffffff',
  black: '#000000',
  transparent: 'transparent'
};

// Spacing tokens based on current spacing system
export const primitiveSpacing: PrimitiveSpacingTokens = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px - --spacing-xs: 0.25rem
  1.5: '0.375rem',  // 6px
  2: '0.5rem',      // 8px - --spacing-sm: 0.5rem
  2.5: '0.625rem',  // 10px
  3: '0.75rem',     // 12px
  3.5: '0.875rem',  // 14px
  4: '1rem',        // 16px - --spacing-md: 1rem
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px - --spacing-lg: 1.5rem
  7: '1.75rem',     // 28px
  8: '2rem',        // 32px - --spacing-xl: 2rem
  9: '2.25rem',     // 36px
  10: '2.5rem',     // 40px
  11: '2.75rem',    // 44px
  12: '3rem',       // 48px - --spacing-2xl: 3rem
  14: '3.5rem',     // 56px
  16: '4rem',       // 64px
  20: '5rem',       // 80px
  24: '6rem',       // 96px
  28: '7rem',       // 112px
  32: '8rem',       // 128px
  36: '9rem',       // 144px
  40: '10rem',      // 160px
  44: '11rem',      // 176px
  48: '12rem',      // 192px
  52: '13rem',      // 208px
  56: '14rem',      // 224px
  60: '15rem',      // 240px
  64: '16rem',      // 256px
  72: '18rem',      // 288px
  80: '20rem',      // 320px
  96: '24rem'       // 384px
};

// Typography tokens based on current typography system
export const primitiveTypography: PrimitiveTypographyTokens = {
  fontFamilies: {
    sans: ['Segoe UI', 'Tahoma', 'Geneva', 'Verdana', 'sans-serif'], // --font-family
    serif: ['ui-serif', 'Georgia', 'Cambria', 'Times New Roman', 'Times', 'serif'],
    mono: ['ui-monospace', 'SFMono-Regular', 'Menlo', 'Monaco', 'Consolas', 'Liberation Mono', 'Courier New', 'monospace']
  },
  fontSizes: {
    xs: '0.7rem',     // --primitive-typography-font-sizes-xs: 0.7rem
    sm: '0.8rem',     // --primitive-typography-font-sizes-sm: 0.8rem  
    base: '0.9rem',   // --primitive-typography-font-sizes-base: 0.9rem
    lg: '1.2rem',     // --primitive-typography-font-sizes-lg: 1.2rem
    xl: '1.5rem',     // --primitive-typography-font-sizes-xl: 1.5rem
    '2xl': '2rem',    // --primitive-typography-font-sizes-2xl: 2rem
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem'
  },
  fontWeights: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,      // --primitive-typography-font-weights-normal: 400
    medium: 500,      // --primitive-typography-font-weights-medium: 500
    semibold: 600,    // --primitive-typography-font-weights-semibold: 600
    bold: 700,        // --primitive-typography-font-weights-bold: 700
    extrabold: 800,
    black: 900
  },
  lineHeights: {
    none: 1,
    tight: 1.2,       // --line-height-tight: 1.2
    snug: 1.375,
    normal: 1.4,      // --line-height-normal: 1.4
    relaxed: 1.6,     // --line-height-relaxed: 1.6
    loose: 2
  },
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em'
  }
};

// Border tokens based on current border system
export const primitiveBorders: PrimitiveBorderTokens = {
  radius: {
    none: '0',
    sm: '4px',        // --primitive-border-radius-sm: 4px
    base: '0.25rem',
    md: '8px',        // --primitive-border-radius-md: 8px  
    lg: '12px',       // --primitive-border-radius-lg: 12px
    xl: '16px',       // --primitive-border-radius-xl: 16px
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px'
  },
  width: {
    0: '0',
    base: '1px',
    2: '2px',
    4: '4px',
    8: '8px'
  }
};

// Shadow tokens based on current shadow system
export const primitiveShadows: PrimitiveShadowTokens = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.1)',      // --primitive-shadows-sm
  base: '0 1px 3px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',      // --primitive-shadows-md
  lg: '0 10px 15px rgba(0, 0, 0, 0.1)',    // --primitive-shadows-lg
  xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
  '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
  none: 'none'
};

// Animation tokens based on current transition system
export const primitiveAnimations: PrimitiveAnimationTokens = {
  duration: {
    75: '75ms',
    100: '100ms',
    150: '150ms',     // --transition-fast: 0.15s (close)
    200: '200ms',
    300: '300ms',     // --transition-normal: 0.3s
    500: '500ms',     // --transition-slow: 0.5s
    700: '700ms',
    1000: '1000ms'
  },
  easing: {
    linear: 'linear',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out'
  }
};

// Z-index tokens based on current z-index system
export const primitiveZIndex: PrimitiveZIndexTokens = {
  auto: 'auto',
  0: 0,
  10: 10,
  20: 20,
  30: 30,
  40: 40,
  50: 50,
  dropdown: 1000,  
  sticky: 1020,     
  fixed: 1030,      
  modal: 1040,      
  popover: 1050,    
  tooltip: 1060     
};

// Main primitive tokens export - combines all token categories
export const primitiveTokens: PrimitiveTokens = {
  colors: primitiveColors,
  spacing: primitiveSpacing,
  typography: primitiveTypography,
  borders: primitiveBorders,
  shadows: primitiveShadows,
  animations: primitiveAnimations,
  zIndex: primitiveZIndex
};
